// Welle 4 — Admin V2 building blocks (modular, reusable across modules).
import React from "react";

export function StatusDot({ level = "healthy", className = "" }) {
  const color =
    level === "critical"
      ? "bg-rose-500"
      : level === "attention"
        ? "bg-amber-400"
        : level === "degraded"
          ? "bg-zinc-600"
          : "bg-emerald-400";
  return (
    <span
      className={`inline-block h-2.5 w-2.5 rounded-full ${color} ${className}`}
      aria-hidden="true"
    />
  );
}

export function SectionCard({ title, icon: Icon, status, actions, children, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5 space-y-4"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon && <Icon size={16} className="text-zinc-500 shrink-0" />}
          <h3 className="text-sm font-semibold tracking-wide text-zinc-200 truncate">
            {title}
          </h3>
          {status && <StatusDot level={status} />}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}

export function StatCard({ title, icon: Icon, value, unit, sub, status = "healthy", testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500">
          {Icon && <Icon size={13} className="shrink-0" />}
          <span className="truncate">{title}</span>
        </div>
        <StatusDot level={status} />
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="text-3xl font-semibold text-zinc-100 tabular-nums">
          {value ?? "—"}
        </span>
        {unit && <span className="text-sm text-zinc-500">{unit}</span>}
      </div>
      {sub && <div className="mt-1.5 text-xs text-zinc-500 leading-snug">{sub}</div>}
    </div>
  );
}

export function ProgressBar({ pct, className = "" }) {
  const v = typeof pct === "number" ? Math.max(0, Math.min(100, pct)) : null;
  const color =
    v == null
      ? "bg-zinc-700"
      : v >= 95
        ? "bg-emerald-400"
        : v >= 80
          ? "bg-amber-400"
          : "bg-rose-400";
  return (
    <div className={`h-1.5 w-full rounded-full bg-zinc-800 overflow-hidden ${className}`}>
      <div
        className={`h-full rounded-full ${color} transition-[width] duration-500`}
        style={{ width: `${v ?? 0}%` }}
      />
    </div>
  );
}

export function EmptyHint({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-dashed border-zinc-800 bg-zinc-950/40 px-4 py-5 text-center text-xs text-zinc-500"
    >
      {children}
    </div>
  );
}

export function Pill({ children, tone = "zinc", className = "", ...rest }) {
  const tones = {
    zinc: "border-zinc-700 bg-zinc-800/60 text-zinc-300",
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    rose: "border-rose-500/40 bg-rose-500/10 text-rose-300",
  };
  return (
    <span
      {...rest}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${tones[tone] || tones.zinc} ${className}`}
    >
      {children}
    </span>
  );
}

export function coverageStatus(pct) {
  if (pct == null) return "degraded";
  if (pct >= 95) return "healthy";
  if (pct >= 80) return "attention";
  return "critical";
}

// Welle 4.1 — 4-stage traffic light (🟢 Vollständig / 🟡 Gut /
// 🟠 Verbesserbar / 🔴 Kritisch) for coverage-style percentages.
export function trafficLevel(pct) {
  if (pct == null) return { key: "unknown", emoji: "⚪", tone: "zinc" };
  if (pct >= 95) return { key: "complete", emoji: "🟢", tone: "emerald" };
  if (pct >= 85) return { key: "good", emoji: "🟡", tone: "amber" };
  if (pct >= 70) return { key: "improvable", emoji: "🟠", tone: "amber" };
  return { key: "critical", emoji: "🔴", tone: "rose" };
}

export function TrafficLightBadge({ pct, label, className = "", testid }) {
  const lvl = trafficLevel(pct);
  return (
    <Pill tone={lvl.tone} className={className} data-testid={testid}>
      <span aria-hidden="true">{lvl.emoji}</span>
      {label}
    </Pill>
  );
}

export function formatDateTime(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString("de-AT", {
      day: "2-digit", month: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}
